import { useDisclosure } from '@chakra-ui/react';
import {
  AssociationEntity,
  IInvoiceRespSchema,
  InvoiceStatusEnum,
} from '@monetize/types/app';
import { FC, ReactNode, useEffect, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import {
  Link,
  Location,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { doPrintInvoiceToPdf } from '../../api/accountsService';
import { useGetInvoiceUnpaidInvoice } from '../../api/invoiceService';
import { useGetById, usePerformEntityAction } from '../../api/queryUtils';
import { ExternalAssociationsIcons } from '../../components/ExternalAssociations/ExternalAssociationsIcons';
import { InvoiceContent } from '../../components/Invoices/InvoiceContent';
import { PreviewInvoicePdfDrawer } from '../../components/Invoices/PreviewInvoicePdfDrawer';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MIDCopyBox,
  MLink,
  MPageContainer,
  MPageHeader,
  MTag,
  MText,
  MTooltip,
} from '../../components/Monetize';
import { DateDisplay } from '../../components/Monetize/DateDisplay';
import { AddPaymentMethodForm } from '../../components/PaymentMethods/Form/AddPaymentMethodForm';
import { ROUTES } from '../../constants';
import { InvoiceStatusEnumDisplay } from '../../constants/invoice';
import { PAPER_PAGE_MAX_WIDTH } from '../../constants/quotes';
import { getAccountInvoicesRoute } from '../../constants/routes';
import { useACL } from '../../services/acl/acl';
import { useToast } from '../../services/toast';
import { downloadAndSaveToPDF } from '../../utils';
import { isInvoicePastDue } from '../../utils/invoices';
import { usePaymentMethodsData } from '../AccountDetails/PaymentMethods/PaymentMethodList';
import { PayInvoiceModal } from './createPayment/CreatePaymentModal';
import { CreditNoteCreateModal } from './CreditNoteCreateModal';
import { EditInvoiceModal } from './EditInvoiceModal';
import { EditInvoiceSection } from './EditInvoiceSection';
import { EditPaymentInstructionsModal } from './EditPaymentInstructionModal';
import { InvoiceActions } from './InvoiceActions';
import InvoiceCreditNotes from './InvoiceCreditNotes';
interface InvoicePageLocation extends Location {
  state: {
    openPaymentModal?: boolean;
    source?: 'dashboard';
  };
}

export const InvoicePage: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { state } = useLocation() as InvoicePageLocation;
  const invoiceId = params.invoiceId || '';
  const paymentModalState = useDisclosure();
  const creditNoteModalState = useDisclosure();
  const { canDo } = useACL();
  const canUpdateBilling = canDo([['billing', 'update']]);
  const { addToast } = useToast();
  const editContactModalState = useDisclosure();
  const {
    open: isOPenEditInvoiceModal,
    onOpen: onOpenEditInvoiceModal,
    onClose: onCloseEditInvoiceModal,
  } = useDisclosure();

  const {
    open: isOpenEditPaymentInstructionsModal,
    onOpen: onOpenEditPaymentInstructionsModal,
    onClose: onCloseEditPaymentInstructionsModal,
  } = useDisclosure();

  const {
    isLoading,
    data: invoice,
    isError,
    refetch,
  } = useGetById<IInvoiceRespSchema>('invoices', invoiceId, {
    enabled: !!invoiceId,
    meta: { showErrorToast: true },
  });

  useEffect(() => {
    if (isError) {
      navigate(ROUTES.SALES_QUOTES_ROUTE, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const { data: unpaidInvoices } = useGetInvoiceUnpaidInvoice(invoiceId, {
    enabled: !!invoiceId,
  });

  const {
    isPending: isTaxRecalculateLoading,
    mutateAsync: performInvoiceAction,
  } = usePerformEntityAction<IInvoiceRespSchema>('invoices', {
    onSuccess: async (_, { action }) => {
      const result = await refetch();
      if (action === 'recalculateTax') {
        if (result.data?.taxError) {
          addToast({
            summary: 'Failed',
            detail: 'Tax could not be recalculated.',
            severity: 'error',
          });
        } else {
          addToast({
            summary: 'Success',
            detail: 'Tax was successfully recalculated.',
            severity: 'success',
          });
        }
      }
    },
    meta: { showErrorToast: true },
  });

  const isDraftInvoice = invoice?.status === InvoiceStatusEnum.DRAFT;
  const isUnpaidInvoice = invoice?.status === InvoiceStatusEnum.UNPAID;

  const paymentMethodsData = usePaymentMethodsData(invoice?.account?.id ?? '');
  const [enableCreditAndRebill, setEnableCreditAndRebill] =
    useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  useEffect(() => {
    if (state?.openPaymentModal) {
      // clear location.state
      navigate(location.pathname, { replace: true });
      paymentModalState.onOpen();
    }
  }, [state, navigate, paymentModalState]);

  const handlePaymentModalClose = (reload: boolean) => {
    reload && refetch();
    paymentModalState.onClose();
  };

  const handleCreditNoteModalClose = (reload: boolean) => {
    // TODO: creaditModal API's should invalidate invoice query
    reload && refetch();
    creditNoteModalState.onClose();
  };

  const handleCreateCreditNote = () => {
    creditNoteModalState.onOpen();
  };

  const handleDownloadPdf = async ({
    id,
    invoiceNumber,
  }: IInvoiceRespSchema) => {
    setIsDownloadingPdf(true);
    try {
      await downloadAndSaveToPDF(() => doPrintInvoiceToPdf(id, invoiceNumber));
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const extraSubtitleParts: ReactNode[] = [];
  if (invoice?.invoiceNumber) {
    extraSubtitleParts.push(
      <MIDCopyBox
        copyValue={invoice.invoiceNumber}
        tooltipMessage="Copy Invoice Number"
      />,
    );
  }
  if (invoice?.lastSent) {
    extraSubtitleParts.push(
      <MFlex>
        <MText fontWeight="semibold" mr={1}>
          Sent:
        </MText>
        <DateDisplay
          date={invoice?.lastSent}
          timezoneConfigKey="userTimezone"
        />
      </MFlex>,
    );
  }

  if (!!invoice?.creditRebilledSourceInvoiceId) {
    extraSubtitleParts.push(
      <MLink
        asChild
        fontSize="sm"
        fontWeight="400"
        _hover={{
          textDecoration: 'underline',
        }}
        pl="2"
      >
        <Link
          to={ROUTES.getInvoiceDetailRoute(
            invoice.creditRebilledSourceInvoiceId,
          )}
        >
          View Original Invoice
        </Link>
      </MLink>,
    );
  }
  if (!!invoice?.creditRebilledTargetInvoiceId) {
    extraSubtitleParts.push(
      <MLink
        asChild
        fontSize="sm"
        fontWeight="400"
        _hover={{
          textDecoration: 'underline',
        }}
        pl="2"
      >
        <Link
          to={ROUTES.getInvoiceDetailRoute(
            invoice.creditRebilledTargetInvoiceId,
          )}
        >
          View Rebilled Invoice
        </Link>
      </MLink>,
    );
  }
  if (invoice) {
    extraSubtitleParts.push(
      <MTooltip label="Download Invoice">
        <MCustomIconButton
          variant="icon"
          btnSize={4}
          p={3}
          _focus={{ bg: 'none' }}
          icon={FiDownload}
          aria-label="Download invoice"
          color="tPurple.dark"
          isLoading={isDownloadingPdf}
          onClick={() => handleDownloadPdf(invoice)}
        />
      </MTooltip>,
    );
  }

  return (
    <MPageContainer height="auto" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title="View Invoice"
        tag={!!invoice?.creditRebilledSourceInvoiceId && 'Rebilled Invoice'}
        tagStyle={{
          color: 'tOrange.base',
          bg: 'tCreamsicle.base',
          px: 2,
          borderRadius: '3px',
        }}
        status={
          invoice?.status ? InvoiceStatusEnumDisplay[invoice?.status] : ''
        }
        migrated={invoice?.migrated}
        parentLink={getAccountInvoicesRoute(
          invoice ? invoice?.account?.id : '',
        )}
        parentLinkTitle={invoice && invoice.account.accountName}
        copyUrl={!invoice?.creditRebilledSourceInvoiceId}
        id={!invoice?.creditRebilledSourceInvoiceId ? invoice?.id : undefined}
        extraSubtitleParts={extraSubtitleParts}
        recordAuditInfo={invoice}
        entityJson={invoice}
        extraBadges={[
          ...(invoice &&
          isInvoicePastDue({ status: invoice.status, dueDate: invoice.dueDate })
            ? [
                <MTag key="PastDueBadge" ml=".5rem" variant="red">
                  Past Due
                </MTag>,
              ]
            : []),
          <ExternalAssociationsIcons
            key="ExternalAssociationsIcons"
            entity={AssociationEntity.INVOICE}
            recordId={invoice?.id || invoiceId}
          />,
        ]}
      >
        <MFlex
          alignContent="center"
          alignItems="center"
          columnGap={3}
          display={['none', 'none', 'flex']}
        >
          {!!invoice?.id && (
            <PreviewInvoicePdfDrawer
              invoiceId={invoice.id}
              invoiceNumber={invoice.invoiceNumber}
            />
          )}
          {isDraftInvoice && !invoice?.taxError && canUpdateBilling && (
            <MButton
              data-testid="pay-invoice-button"
              variant="primary"
              isLoading={isTaxRecalculateLoading || isLoading}
              disabled={isTaxRecalculateLoading || isLoading}
              onClick={() => {
                performInvoiceAction({
                  id: invoiceId,
                  action: 'finalize',
                });
              }}
            >
              Finalize
            </MButton>
          )}
          {isDraftInvoice && invoice?.taxError && canUpdateBilling && (
            <MButton
              data-testid="pay-invoice-button"
              variant="primary"
              isLoading={isTaxRecalculateLoading}
              disabled={isTaxRecalculateLoading}
              onClick={() => {
                performInvoiceAction({
                  id: invoiceId,
                  action: 'recalculateTax',
                });
              }}
            >
              Recalculate Tax
            </MButton>
          )}
          {isUnpaidInvoice &&
            !invoice?.paid &&
            invoice.amount > 0 &&
            canUpdateBilling && (
              <MButton
                data-testid="pay-invoice-button"
                variant="primary"
                onClick={paymentModalState.onOpen}
              >
                Pay Invoice
              </MButton>
            )}
          {invoice && (
            <InvoiceActions
              editContactModalState={editContactModalState}
              isReadOnly={!canUpdateBilling}
              invoice={invoice}
              onCreateCreditNote={handleCreateCreditNote}
              refreshData={refetch}
              onOpenCreditAndRebillModal={() => {
                onOpenEditInvoiceModal();
                setEnableCreditAndRebill(true);
              }}
              onOpenPaymentInstructionsModal={() => {
                onOpenEditPaymentInstructionsModal();
              }}
              recalculateTax={() =>
                performInvoiceAction({
                  id: invoice.id,
                  action: 'recalculateTax',
                })
              }
              source={state?.source}
            />
          )}
        </MFlex>
      </MPageHeader>

      <MBox
        width="100%"
        px={[1, 1, 8]}
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <MBox maxW={PAPER_PAGE_MAX_WIDTH} w={'full'}>
          {invoice?.id && (
            <EditInvoiceSection
              isDraftInvoice={isDraftInvoice}
              isLoading={isLoading}
              invoice={invoice}
              customFiledValues={invoice?.customFields}
              onOpenEditInvoiceModal={onOpenEditInvoiceModal}
            />
          )}
          <InvoiceContent
            loadingInvoice={isLoading}
            unpaidInvoices={unpaidInvoices}
            invoice={invoice}
            handleOpenEditContactModal={() => editContactModalState.onOpen()}
          />
        </MBox>
        {isOPenEditInvoiceModal && invoice && (
          <EditInvoiceModal
            isDraftInvoice={isDraftInvoice}
            isCreditAnRebillPossible={!invoice?.creditRebilledTargetInvoiceId}
            enableCreditAndRebill={enableCreditAndRebill}
            invoice={invoice}
            isOpen={isOPenEditInvoiceModal}
            handleClose={() => {
              onCloseEditInvoiceModal();
              refetch();
              setEnableCreditAndRebill(false);
            }}
          />
        )}
        {isOpenEditPaymentInstructionsModal && invoice && (
          <EditPaymentInstructionsModal
            invoice={invoice}
            isOpen={isOpenEditPaymentInstructionsModal}
            handleClose={() => {
              onCloseEditPaymentInstructionsModal();
              refetch();
            }}
          />
        )}
        {paymentMethodsData.isModalOpen && !!invoice && (
          <>
            <InvoiceCreditNotes invoice={invoice} />
            <AddPaymentMethodForm
              isOpen={paymentMethodsData.isModalOpen}
              onClose={paymentMethodsData.onCloseModal}
              accountId={invoice.account.id}
            />
          </>
        )}
        {paymentModalState.open && !!invoice && (
          <PayInvoiceModal
            isOpen={paymentModalState.open}
            invoice={invoice}
            onClose={handlePaymentModalClose}
          />
        )}
        {creditNoteModalState.open && !!invoice && (
          <CreditNoteCreateModal
            isOpen={creditNoteModalState.open}
            invoice={invoice}
            onClose={handleCreditNoteModalClose}
          />
        )}
      </MBox>
    </MPageContainer>
  );
};
